/////////////////////////////////////////////////////////////////////
// Seller-defined ("custom") product variants.                      //
//                                                                  //
// util/productVariants.js turns the size/volume/scent/colour fields //
// an operator set up in Sharetribe Console into dropdowns. Those    //
// can't cover every product a brand might sell — kids' clothes in   //
// "Age 1–2, Age 2–3", a print in "A4, A3", a candle in "Wick: …" —  //
// and asking an operator to add a Console field for each one        //
// doesn't scale. So a vendor can name their own variants on the     //
// Details tab and list the options for each.                        //
//                                                                  //
// Where they live                                                  //
// ---------------                                                  //
// The variants are stored in the listing's publicData under         //
// `customVariants`:                                                 //
//                                                                  //
//   [{ key: 'custom_k3j9x2', label: 'Age', options: ['1–2', '2–3'] }]//
//                                                                  //
// getProductVariantFields() appends them after the Console fields,  //
// in the same { key, label, options } shape, which is all the rest  //
// of the app needs: per-variation stock, the product page chips,    //
// the basket, checkout and the order details all treat them exactly //
// like a size or a colour.                                          //
//                                                                  //
// Why a generated key                                              //
// -------------------                                              //
// Stock is counted per combination of `key:value` pairs. The key is //
// generated once, when the variant is first saved, and never        //
// changes, so a vendor can rename "Age" to "Age range" without      //
// losing the counts they entered against it. Options are their own  //
// value (as with Console text fields), so renaming an option is the //
// same as removing it and adding a new one.                         //
/////////////////////////////////////////////////////////////////////

/** publicData key the variants are stored under. */
export const CUSTOM_VARIANTS_KEY = 'customVariants';

// Console field keys are free text, so the prefix keeps the two apart. The
// key ends up in form field names (productVariants.<key>) and in stock
// combination keys ("custom_k3j9x2:Age 1-2|…"), so it is kept to characters
// that mean nothing to either.
export const CUSTOM_VARIANT_KEY_PREFIX = 'custom_';
const CUSTOM_VARIANT_KEY_PATTERN = /^custom_[a-z0-9]+$/;

// Generous enough for anything real, small enough that the stock grid on the
// next tab (one input per combination) stays something a vendor can fill in.
export const MAX_CUSTOM_VARIANTS = 5;
export const MAX_CUSTOM_VARIANT_OPTIONS = 30;
export const CUSTOM_VARIANT_LABEL_MAX_LENGTH = 40;
export const CUSTOM_VARIANT_OPTION_MAX_LENGTH = 40;

// "|" separates the parts of a stock combination key, so it can't appear in an
// option. A slash reads the same to a shopper ("Red|Blue" → "Red/Blue").
const COMBINATION_SEPARATOR_PATTERN = /\|/g;

/**
 * Tidy one name the vendor typed: trim it, collapse runs of whitespace and
 * keep it to the maximum length.
 *
 * @param {*} value the raw value
 * @param {number} maxLength the longest the result may be
 * @returns {string} the cleaned text ('' for anything that isn't text)
 */
export const cleanCustomVariantText = (value, maxLength) =>
  typeof value === 'string'
    ? value
        .replace(COMBINATION_SEPARATOR_PATTERN, '/')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, maxLength)
        .trim()
    : '';

// "Red" and "red" would be two stock counts and two chips that look the same
// to a shopper, so names are compared case-insensitively.
const toComparable = value => cleanCustomVariantText(value, Infinity).toLowerCase();

/**
 * The options of one variant, cleaned, in the vendor's order, without blanks
 * or repeats.
 *
 * @param {Array} options the raw option names
 * @returns {Array<string>} at most MAX_CUSTOM_VARIANT_OPTIONS options
 */
export const cleanCustomVariantOptions = options => {
  const cleaned = (Array.isArray(options) ? options : [])
    .map(option => cleanCustomVariantText(option, CUSTOM_VARIANT_OPTION_MAX_LENGTH))
    .filter(Boolean);
  const unique = cleaned.filter(
    (option, i) => cleaned.findIndex(other => toComparable(other) === toComparable(option)) === i
  );
  return unique.slice(0, MAX_CUSTOM_VARIANT_OPTIONS);
};

/**
 * Is this a key a custom variant could have been saved under?
 *
 * @param {string} key a variant field key
 * @returns {boolean} true for keys generated by createCustomVariantKey
 */
export const isCustomVariantKey = key =>
  typeof key === 'string' && CUSTOM_VARIANT_KEY_PATTERN.test(key);

/**
 * A new key, different from every key in `existingKeys`.
 *
 * @param {Array<string>} existingKeys keys already in use on the listing
 * @param {Function} [random] source of randomness, for tests
 * @returns {string} e.g. "custom_k3j9x2"
 */
export const createCustomVariantKey = (existingKeys = [], random = Math.random) => {
  let key;
  do {
    const id = random()
      .toString(36)
      .slice(2, 8)
      .replace(/[^a-z0-9]/g, '');
    key = `${CUSTOM_VARIANT_KEY_PREFIX}${id || '0'}`;
  } while (existingKeys.includes(key));
  return key;
};

/**
 * Read the custom variants saved on a listing, ignoring anything malformed.
 *
 * @param {Object} publicData listing.attributes.publicData
 * @returns {Array<Object>} [{ key, label, options: [string] }]
 */
export const getCustomVariants = publicData => {
  const raw = publicData?.[CUSTOM_VARIANTS_KEY];
  if (!Array.isArray(raw)) {
    return [];
  }
  const seenKeys = [];
  const seenLabels = [];
  return raw
    .reduce((acc, variant) => {
      const key = variant?.key;
      const label = cleanCustomVariantText(variant?.label, CUSTOM_VARIANT_LABEL_MAX_LENGTH);
      const options = cleanCustomVariantOptions(variant?.options);
      const isUsable =
        isCustomVariantKey(key) &&
        !seenKeys.includes(key) &&
        label &&
        !seenLabels.includes(label.toLowerCase()) &&
        options.length > 0;
      if (!isUsable) {
        return acc;
      }
      seenKeys.push(key);
      seenLabels.push(label.toLowerCase());
      return acc.concat({ key, label, options });
    }, [])
    .slice(0, MAX_CUSTOM_VARIANTS);
};

/**
 * The custom variants as variant fields, in the shape getProductVariantFields
 * returns, so everything downstream can treat them like any other variant.
 *
 * @param {Object} publicData listing.attributes.publicData
 * @returns {Array<Object>} [{ key, label, options: [{ value, label }], isCustom }]
 */
export const getCustomVariantFields = publicData =>
  getCustomVariants(publicData).map(({ key, label, options }) => ({
    key,
    label,
    options: options.map(option => ({ value: option, label: option })),
    isCustom: true,
  }));

/**
 * Initial values for the Details form's custom variant editor.
 *
 * @param {Object} publicData listing.attributes.publicData
 * @returns {Array<Object>} [{ key, label, options: [string] }]
 */
export const getCustomVariantsInitialValues = publicData =>
  getCustomVariants(publicData).map(({ key, label, options }) => ({
    key,
    label,
    options: [...options],
  }));

/**
 * Turn the Details form's custom variant editor into what is saved.
 *
 * Rows the vendor left completely empty are dropped. Variants that are still
 * missing a name or options can't reach here — the form won't submit — but are
 * dropped too rather than saved half-made. Variants added in this session get
 * a key; existing ones keep theirs, which is what keeps their stock counts.
 *
 * @param {Array} formVariants [{ key?, label, options: [string] }]
 * @param {Function} [random] source of randomness for new keys, for tests
 * @returns {Array<Object>|null} the variants to save, or null when there are none
 *   (null removes the key from publicData)
 */
export const formatCustomVariantsForSave = (formVariants, random) => {
  const variants = (Array.isArray(formVariants) ? formVariants : []).map(variant => ({
    key: variant?.key,
    label: cleanCustomVariantText(variant?.label, CUSTOM_VARIANT_LABEL_MAX_LENGTH),
    options: cleanCustomVariantOptions(variant?.options),
  }));
  const complete = variants.filter(v => v.label && v.options.length > 0);

  const usedKeys = [];
  const saved = complete.slice(0, MAX_CUSTOM_VARIANTS).map(variant => {
    const keepKey = isCustomVariantKey(variant.key) && !usedKeys.includes(variant.key);
    const key = keepKey
      ? variant.key
      : createCustomVariantKey(usedKeys.concat(complete.map(v => v.key)), random);
    usedKeys.push(key);
    return { ...variant, key };
  });

  return saved.length > 0 ? saved : null;
};

/**
 * Is `value` the same name as another entry in `values`? Used by the editor to
 * flag a repeated variant name or option as the vendor types.
 *
 * @param {string} value the name being checked
 * @param {Array<string>} values every name in the same list, including `value`
 * @param {number} index where `value` sits in `values`
 * @returns {boolean} true when an earlier or later entry has the same name
 */
export const isDuplicateCustomVariantName = (value, values = [], index) => {
  const comparable = toComparable(value);
  return (
    comparable !== '' &&
    (Array.isArray(values) ? values : []).some(
      (other, i) => i !== index && toComparable(other) === comparable
    )
  );
};
